import { useState, useMemo, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { type ColumnDef, type SortingState, type RowSelectionState } from "@tanstack/react-table"
import { Plus, Search, Users, Upload } from "lucide-react"
import { useContacts } from "@/hooks/use-contacts"
import { ApiErrorState } from "@/components/api-error-state"
import { useCustomFields } from "@/hooks/use-custom-fields"
import { useCustomFieldColumns } from "@/hooks/use-custom-field-columns"
import { useColumnPreferences } from "@/hooks/use-column-preferences"
import { useBulkDelete, useBulkUpdate, useBulkTag } from "@/hooks/use-bulk-actions"
import { Button } from "@/components/ui/button"
import { DataTable } from "@/components/data-table"
import { ColumnSelector, type ColumnOption } from "@/components/column-selector"
import { SortableHeader } from "@/components/sortable-header"
import { EmptyState } from "@/components/empty-state"
import { BulkActionBar, buildDeleteAction, buildTagAction, buildAssignAction } from "@/components/bulk-action-bar"
import { BulkDeleteDialog } from "@/components/bulk-delete-dialog"
import { BulkAssignDialog } from "@/components/bulk-assign-dialog"
import { BulkTagDialog } from "@/components/bulk-tag-dialog"
import { useFilterParams } from "@/hooks/use-filter-params"
import { FilterPanel, TextFilter, DateRangeFilter } from "@/components/filters"
import { ImportWizard } from "@/components/import-wizard"
import { ExportButton } from "@/components/export-button"
import { createSelectColumn } from "@/lib/bulk-selection"
import { LoadMoreButton } from "@/components/load-more-button"
import { formatDate, sortingStateToParam, buildDetailUrl } from "@/lib/utils"
import { toast } from "sonner"
import type { Contact } from "@/lib/types"

const CONTACT_FILTER_KEYS = [
  "owner_id", "department", "created_at_from", "created_at_to",
] as const

const BASE_COLUMNS: ColumnDef<Contact, unknown>[] = [
  {
    accessorKey: "full_name",
    header: ({ column }) => <SortableHeader column={column}>Imie i nazwisko</SortableHeader>,
    cell: ({ row }) => (
      <div>
        <div className="font-medium text-foreground">{row.original.full_name}</div>
        {row.original.title && (
          <div className="text-xs text-muted-foreground">
            {row.original.title}
          </div>
        )}
      </div>
    ),
  },
  {
    accessorKey: "companies",
    header: "Firma",
    enableSorting: false,
    cell: ({ row }) => {
      const companies = row.original.companies
      if (!companies?.length) return <span className="text-muted-foreground">{"\u2014"}</span>
      return (
        <div className="text-sm">
          {companies[0].name}
          {companies.length > 1 && (
            <span className="text-xs text-muted-foreground">
              {" "}+{companies.length - 1}
            </span>
          )}
        </div>
      )
    },
  },
  {
    accessorKey: "department",
    header: ({ column }) => <SortableHeader column={column}>Dzial</SortableHeader>,
    cell: ({ row }) => <span className="text-muted-foreground">{row.original.department || "\u2014"}</span>,
  },
  {
    accessorKey: "email",
    header: ({ column }) => <SortableHeader column={column}>Email</SortableHeader>,
    cell: ({ row }) => (
      <span className="text-sm">{row.original.email || "\u2014"}</span>
    ),
  },
  {
    accessorKey: "phone",
    header: ({ column }) => <SortableHeader column={column}>Telefon</SortableHeader>,
    cell: ({ row }) => (
      <span className="text-sm">{row.original.phone || "\u2014"}</span>
    ),
  },
  {
    accessorKey: "owner",
    header: "Opiekun",
    enableSorting: false,
    cell: ({ row }) =>
      row.original.owner ? (
        <span className="text-sm">
          {row.original.owner.first_name} {row.original.owner.last_name}
        </span>
      ) : (
        <span className="text-muted-foreground">{"\u2014"}</span>
      ),
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => <SortableHeader column={column}>Utworzono</SortableHeader>,
    cell: ({ row }) => (
      <span className="text-xs text-muted-foreground">
        {formatDate(row.original.created_at)}
      </span>
    ),
  },
]

const DEFAULT_VISIBLE = ["full_name", "companies", "department", "email", "phone", "owner", "created_at"]

const BASE_COLUMN_LABELS: Record<string, string> = {
  full_name: "Imie i nazwisko",
  companies: "Firma",
  department: "Dzial",
  email: "Email",
  phone: "Telefon",
  owner: "Opiekun",
  created_at: "Utworzono",
}

export function ContactsListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showAssignDialog, setShowAssignDialog] = useState(false)
  const [showTagDialog, setShowTagDialog] = useState(false)
  const [showImportWizard, setShowImportWizard] = useState(false)
  const [cursor, setCursor] = useState<string | undefined>()
  const [allContacts, setAllContacts] = useState<Contact[]>([])
  const prevKeyRef = useRef("")

  const { filters, setFilter, clearFilters, activeFilterCount } = useFilterParams(CONTACT_FILTER_KEYS)

  const sortParam = sortingStateToParam(sorting)
  const { data, isLoading, isFetching, isError, error, refetch } = useContacts({
    search: debouncedSearch,
    sort: sortParam,
    cursor,
    ...filters,
  })

  const filterKey = JSON.stringify({ search: debouncedSearch, sort: sortParam, ...filters })
  useEffect(() => {
    if (filterKey !== prevKeyRef.current) {
      prevKeyRef.current = filterKey
      setCursor(undefined)
      setAllContacts([])
    }
  }, [filterKey])

  useEffect(() => {
    if (data?.data) {
      if (cursor) {
        setAllContacts((prev) => [...prev, ...data.data])
      } else {
        setAllContacts(data.data)
      }
    }
  }, [data])

  const bulkDeleteMutation = useBulkDelete("contacts")
  const bulkUpdateMutation = useBulkUpdate("contacts")
  const bulkTagMutation = useBulkTag("contacts")

  // Custom fields
  const { data: cfData } = useCustomFields("contacts")
  const customFields = cfData?.data
  const customFieldCols = useCustomFieldColumns<Contact>(customFields)

  const allColumns = useMemo(
    () => [createSelectColumn<Contact>(), ...BASE_COLUMNS, ...customFieldCols] as ColumnDef<Contact, unknown>[],
    [customFieldCols]
  )

  const allColumnIds = useMemo(() => {
    const baseIds = BASE_COLUMNS.map(
      (c) => (c as { accessorKey?: string }).accessorKey ?? (c as { id?: string }).id ?? ""
    )
    const cfIds = customFieldCols.map((c) => (c as { id?: string }).id ?? "")
    return [...baseIds, ...cfIds]
  }, [customFieldCols])

  const columnOptions: ColumnOption[] = useMemo(() => {
    const base = Object.keys(BASE_COLUMN_LABELS).map((id) => ({
      id,
      label: BASE_COLUMN_LABELS[id],
    }))
    const cf = (customFields ?? []).map((f) => ({
      id: `cf_${f.key}`,
      label: f.label?.pl || f.label?.en || f.key,
      isCustomField: true,
    }))
    return [...base, ...cf]
  }, [customFields])

  const {
    columnVisibility,
    setColumnVisibility,
    visibleCount,
    totalCount,
    toggleColumn,
    showAll,
    hideAll,
    isColumnVisible,
  } = useColumnPreferences("contacts", allColumnIds, DEFAULT_VISIBLE)

  const handleSearchChange = (value: string) => {
    setSearch(value)
    clearTimeout((window as unknown as Record<string, unknown>).__contactSearchTimeout as number)
    ;(window as unknown as Record<string, unknown>).__contactSearchTimeout = setTimeout(() => {
      setDebouncedSearch(value)
    }, 300)
  }

  const contacts = allContacts
  const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k])
  const selectedCount = selectedIds.length

  const handleBulkDelete = async () => {
    const result = await bulkDeleteMutation.mutateAsync({ ids: selectedIds })
    toast.success(`Usunieto ${result.deleted} kontaktow`)
    setRowSelection({})
  }

  const handleBulkAssign = async (userId: string) => {
    const result = await bulkUpdateMutation.mutateAsync({
      ids: selectedIds,
      changes: { owner_id: userId },
    })
    toast.success(`Zaktualizowano ${result.updated} kontaktow`)
    setRowSelection({})
  }

  const handleBulkTag = async (tagNames: string[]) => {
    const result = await bulkTagMutation.mutateAsync({
      ids: selectedIds,
      tag_names: tagNames,
    })
    toast.success(`Otagowano ${result.tagged} kontaktow`)
    setRowSelection({})
  }

  const bulkActions = [
    buildAssignAction(() => setShowAssignDialog(true), "Przypisz opiekuna"),
    buildTagAction(() => setShowTagDialog(true)),
    buildDeleteAction(() => setShowDeleteDialog(true)),
  ]

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={20} className="text-primary" />
          <h1 className="page-title">
            {t("contacts.title")}
          </h1>
          {data && (
            <span className="text-sm text-muted-foreground">
              ({data.data.length}{data.meta.has_more ? "+" : ""})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            entity="contacts"
            filters={{ search: debouncedSearch, ...filters }}
            visibleColumns={Object.keys(columnVisibility).filter((k) => columnVisibility[k] !== false)}
          />
          <Button variant="outline" onClick={() => setShowImportWizard(true)}>
            <Upload size={16} />
            Importuj CSV
          </Button>
          <Button onClick={() => navigate("/contacts/new")}>
            <Plus size={16} />
            {t("contacts.new")}
          </Button>
        </div>
      </div>

      {/* Search + Column selector */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t("common.search")}
            className="h-9 w-full rounded-lg border border-input bg-transparent pl-9 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        <ColumnSelector
          columns={columnOptions}
          visibleCount={visibleCount}
          totalCount={totalCount}
          isColumnVisible={isColumnVisible}
          onToggleColumn={toggleColumn}
          onShowAll={showAll}
          onHideAll={hideAll}
        />
      </div>

      {/* Filters */}
      <FilterPanel activeCount={activeFilterCount} onClear={clearFilters}>
        <TextFilter
          label="Dzial"
          value={filters.department || ""}
          onChange={(v) => setFilter("department", v)}
          placeholder="Wpisz nazwe dzialu"
        />
        <DateRangeFilter
          label="Data utworzenia"
          fromValue={filters.created_at_from || ""}
          toValue={filters.created_at_to || ""}
          onFromChange={(v) => setFilter("created_at_from", v)}
          onToChange={(v) => setFilter("created_at_to", v)}
        />
      </FilterPanel>

      {/* Bulk action bar */}
      <BulkActionBar
        selectedCount={selectedCount}
        onDeselectAll={() => setRowSelection({})}
        actions={bulkActions}
      />

      {/* Table / Empty state / Error state */}
      {isError ? (
        <ApiErrorState error={error} onRetry={() => refetch()} />
      ) : !isLoading && !debouncedSearch && data && data.data.length === 0 ? (
        <EmptyState
          icon={Users}
          heading="Brak osob kontaktowych"
          description="Dodaj pierwsza osobe"
          actionLabel="Nowa osoba"
          onAction={() => navigate("/contacts/new")}
        />
      ) : (
        <DataTable
          columns={allColumns}
          data={contacts}
          isLoading={isLoading}
          onRowClick={(contact) => navigate(buildDetailUrl("/contacts", contact.id, { sort: sortingStateToParam(sorting), filters: filters as Record<string, string | undefined> }))}
          emptyMessage={t("common.noResults")}
          sorting={sorting}
          onSortingChange={setSorting}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          getRowId={(row) => row.id}
        />
      )}

      <LoadMoreButton
        hasMore={data?.meta.has_more ?? false}
        isLoading={isFetching && !!cursor}
        onClick={() => {
          if (data?.meta.next_cursor) setCursor(data.meta.next_cursor)
        }}
      />
      {/* Bulk action dialogs */}
      <BulkDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        count={selectedCount}
        entityLabel="kontaktow"
        onConfirm={handleBulkDelete}
      />
      <BulkAssignDialog
        open={showAssignDialog}
        onOpenChange={setShowAssignDialog}
        count={selectedCount}
        onConfirm={handleBulkAssign}
        fieldLabel="Opiekun"
      />
      <BulkTagDialog
        open={showTagDialog}
        onOpenChange={setShowTagDialog}
        count={selectedCount}
        onConfirm={handleBulkTag}
      />
      <ImportWizard
        open={showImportWizard}
        onOpenChange={setShowImportWizard}
        defaultEntityType="contacts"
      />
    </div>
  )
}
